import 'chart.js/auto'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { fetchSchoolOverview } from '../api/dashboard'
import Header from '../components/Header'
import HistoryGraphModal from '../components/HistoryGraphModal'
import Sidebar from '../components/Sidebar'
import SpeedtestModalWebSocket from '../components/SpeedtestModalWebSocket'
import {
	DevicesTable,
	MapSection,
	StatsCards,
	TopConsumersTable,
	TrafficOverview,
} from '../components/dashboard'
import { useDashboardData } from '../hooks/useDashboardData'
//TODO: сменить все что связано с Logs, это старая тема.g
const buildDeviceChartData = (series, t) => {
	if (!series?.in?.length || !series?.out?.length) {
		return null
	}

	return {
		labels: series.in.map(point =>
			new Date(point[0] * 1000).toLocaleString('ru-KZ', {
				timeZone: 'Asia/Almaty',
				hour: '2-digit',
				minute: '2-digit',
			}),
		),
		datasets: [
			{
				label: t('dashboard.incomingTraffic'),
				data: series.in.map(point => parseFloat(point[1])),
				borderColor: 'rgb(42, 209, 0)',
				backgroundColor: 'rgba(42, 209, 0, 0.2)',
				fill: false,
				tension: 0.4,
			},
			{
				label: t('dashboard.outgoingTraffic'),
				data: series.out.map(point => parseFloat(point[1])),
				borderColor: 'rgb(255, 173, 8)',
				backgroundColor: 'rgba(255, 173, 8, 0.2)',
				fill: false,
				tension: 0.4,
			},
		],
	}
}

const getDeviceIdentifier = device =>
	device?.ip_address || device?.ip || device?.ipAddress || ''

const Dashboard = ({ setIsAuthenticated, onLogout, role, userEmail }) => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	// На мобильных устройствах sidebar скрыт по умолчанию
	const [isSidebarVisible, setSidebarVisible] = useState(() => {
		if (typeof window !== 'undefined') {
			return window.innerWidth >= 1024 // lg breakpoint
		}
		return true
	})
	const [timeRange, setTimeRange] = useState('24h')
	const [logsTimeRange, setLogsTimeRange] = useState('24h')
	const [selectedDevice, setSelectedDevice] = useState('')
	const [isLogsModalOpen, setIsLogsModalOpen] = useState(false)
	const [deviceChartData, setDeviceChartData] = useState(null)
	const [logsStatus, setLogsStatus] = useState('')
	const [logsLoading, setLogsLoading] = useState(false)
	const [logsReloadKey, setLogsReloadKey] = useState(0)
	const [selectedSpeedtestDevice, setSelectedSpeedtestDevice] = useState(null)
	const [devicesFilterRequest, setDevicesFilterRequest] = useState(null)
	const trafficSectionRef = useRef(null)

	const focusDevices = filters =>
		setDevicesFilterRequest({ filters, id: Date.now() })
	const focusTraffic = range => {
		if (range) setTimeRange(range)
		trafficSectionRef.current?.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
		})
	}

	const statActions = {
		online: () => focusDevices({ status: 'down' }),
		load: () => focusTraffic(),
		speed: () => navigate('/speedtest-results'),
		traffic: () => focusTraffic('24h'),
	}

	const {
		devices,
		topConsumers,
		schoolMarkers,
		onlineCount,
		totalDevices,
		averageLoadPercent,
		avgSpeedtest,
		utilizationData,
		alerts,
		trends,
		currentTraffic,
		dailyStats,
		summaryLoading,
		utilizationLoading,
		alertsLoading,
		alertsPage,
		alertsPageSize,
		alertsTotalPages,
		alertsTotalCount,
		acknowledgeLoading,
		acknowledgeAlert,
		reloadSummary,
		fetchDeviceSeries,
		startSpeedtest,
		setAlertsPage,
		setAlertsPageSize,
	} = useDashboardData(timeRange)

	const toggleSidebar = () => setSidebarVisible(prev => !prev)

	const handleLogout = () => {
		if (typeof onLogout === 'function') {
			onLogout()
			return
		}

		localStorage.removeItem('authToken')
		setIsAuthenticated(false)
		navigate('/')
	}

	const handleOpenLogs = deviceIp => {
		setSelectedDevice(deviceIp)
		setDeviceChartData(null)
		setLogsStatus('')
		setLogsReloadKey(Date.now())
		setIsLogsModalOpen(true)
	}

	const handleCloseLogs = () => {
		setIsLogsModalOpen(false)
		setSelectedDevice('')
		setDeviceChartData(null)
		setLogsStatus('')
	}

	useEffect(() => {
		if (!isLogsModalOpen || !selectedDevice) {
			return
		}

		let isCancelled = false

		const loadLogs = async () => {
			setLogsLoading(true)
			setLogsStatus(t('dashboard.loadingData'))

			try {
				const series = await fetchDeviceSeries(selectedDevice, logsTimeRange)
				if (isCancelled) return

				const chartData = buildDeviceChartData(series, t)
				setDeviceChartData(chartData)
				setLogsStatus(chartData ? '' : t('common.noDataToDisplay'))
			} catch (error) {
				if (!isCancelled) {
					setDeviceChartData(null)
					setLogsStatus(t('dashboard.errorLoadingDevice'))
				}
			} finally {
				if (!isCancelled) {
					setLogsLoading(false)
				}
			}
		}

		loadLogs()

		return () => {
			isCancelled = true
		}
	}, [
		fetchDeviceSeries,
		isLogsModalOpen,
		logsReloadKey,
		logsTimeRange,
		selectedDevice,
	])

	const handleOpenSpeedtest = device => {
		setSelectedSpeedtestDevice(device)
	}

	const handleCloseSpeedtest = () => {
		setSelectedSpeedtestDevice(null)
	}

	// Получаем имя устройства для SpeedtestModal
	const selectedDeviceName = selectedSpeedtestDevice
		? selectedSpeedtestDevice.school ||
			selectedSpeedtestDevice.name ||
			getDeviceIdentifier(selectedSpeedtestDevice) ||
			t('common.unknownDevice')
		: ''

	// Получаем имя устройства для HistoryGraphModal по IP
	const selectedDeviceForLogs = devices.find(
		device => getDeviceIdentifier(device) === selectedDevice,
	)
	const logsDeviceName = selectedDeviceForLogs
		? selectedDeviceForLogs.school ||
			selectedDeviceForLogs.name ||
			selectedDevice ||
			t('common.unknownDevice')
		: selectedDevice || t('common.unknownDevice')

	const handleOpenDetails = async device => {
		const ipAddress = getDeviceIdentifier(device)
		if (!ipAddress) {
			return
		}

		try {
			const overviewData = await fetchSchoolOverview(ipAddress)
			navigate(`/schools/${encodeURIComponent(ipAddress)}`, {
				state: {
					ip: ipAddress,
					school: overviewData?.school || device.school,
					overview: overviewData,
				},
			})
		} catch (error) {
			navigate(`/schools/${encodeURIComponent(ipAddress)}`, {
				state: {
					ip: ipAddress,
					school: device.school,
				},
			})
		}
	}

	return (
		<div className='relative isolate flex min-h-screen flex-col overflow-clip bg-surface-base text-muted-100'>
			<div className='absolute inset-0 -z-10 bg-brand-radial opacity-50' />
			<Header
				isSidebarVisible={isSidebarVisible}
				toggleSidebar={toggleSidebar}
				onLogout={handleLogout}
				userEmail={userEmail}
				userRole={role}
			/>
			<div className='flex flex-1'>
				<Sidebar
					onLogout={handleLogout}
					userRole={role}
					isSidebarVisible={isSidebarVisible}
					onClose={() => setSidebarVisible(false)}
				/>
				<main className='relative flex min-w-0 flex-1 flex-col gap-4 sm:gap-6 bg-surface-base/60 px-3 py-4 sm:px-4 sm:py-6 transition-all duration-300 ease-brand lg:px-10'>
					<StatsCards
						onlineCount={onlineCount}
						totalDevices={totalDevices}
						averageLoadPercent={averageLoadPercent}
						avgSpeedtest={avgSpeedtest}
						trends={trends}
						currentTraffic={currentTraffic}
						isLoading={summaryLoading}
						actions={statActions}
					/>

					<section
						id='map'
						className='grid scroll-mt-24 grid-cols-1 gap-4 xl:grid-cols-12'
					>
						<MapSection markers={schoolMarkers} />
					</section>

					<section
						ref={trafficSectionRef}
						className='grid scroll-mt-24 grid-cols-1 gap-4 xl:grid-cols-12'
					>
						<div className='w-full xl:col-span-8'>
							<TrafficOverview
								timeRange={timeRange}
								onChangeTimeRange={setTimeRange}
								series={utilizationData}
								isLoading={utilizationLoading}
							/>
						</div>
						<div className='w-full xl:col-span-4'>
							<TopConsumersTable
								rows={topConsumers}
								onViewAll={() => focusDevices({ trafficFilter: 'topDownload' })}
							/>
						</div>
					</section>

					<DevicesTable
						devices={devices}
						onLogsClick={handleOpenLogs}
						onSpeedtestClick={handleOpenSpeedtest}
						userRole={role}
						onSchoolCreated={reloadSummary}
						onDetailsClick={handleOpenDetails}
						className='mb-6'
						filterRequest={devicesFilterRequest}
					/>
				</main>
			</div>

			<HistoryGraphModal
				isOpen={isLogsModalOpen}
				onClose={handleCloseLogs}
				deviceName={logsDeviceName}
				deviceIP={selectedDevice}
				logsContent={logsStatus}
				timeRange={logsTimeRange}
				onTimeRangeChange={setLogsTimeRange}
				chartData={deviceChartData}
				isLoading={logsLoading}
			/>

			<SpeedtestModalWebSocket
				isOpen={Boolean(selectedSpeedtestDevice)}
				onClose={handleCloseSpeedtest}
				deviceName={selectedDeviceName}
				deviceIP={getDeviceIdentifier(selectedSpeedtestDevice) || ''}
			/>
		</div>
	)
}

export default Dashboard
